export default class MultiKeyMap<K extends readonly any[], V> implements Map<K, V> {
  private map = new Map<string, [K, V]>();

  private keyToString(key: K): string {
    return JSON.stringify(key);
  }

  set(key: K, value: V): this {
    this.map.set(this.keyToString(key), [key, value]);
    return this;
  }

  get(key: K): V | undefined {
    return this.map.get(this.keyToString(key))?.[1];
  }

  has(key: K): boolean {
    return this.map.has(this.keyToString(key));
  }

  delete(key: K): boolean {
    return this.map.delete(this.keyToString(key));
  }

  clear(): void {
    this.map.clear();
  }

  keys(): MapIterator<K> {
    const map = this.map;
    return (function* (): Generator<K, undefined, unknown> {
      for (const [key] of map.values()) {
        yield key;
      }
    })();
  }

  values(): MapIterator<V> {
    const map = this.map;
    return (function* (): Generator<V, undefined, unknown> {
      for (const [, value] of map.values()) {
        yield value;
      }
    })();
  }

  entries(): MapIterator<[K, V]> {
    return this.map.values();
  }

  [Symbol.iterator](): MapIterator<[K, V]> {
    return this.entries();
  }

  get size(): number {
    return this.map.size;
  }

  forEach(callbackfn: (value: V, key: K, map: Map<K, V>) => void, thisArg?: any): void {
    for (const [key, value] of this.map.values()) {
      callbackfn.call(thisArg, value, key, this);
    }
  }

  getOrInsert(key: K, defaultValue: V): V {
    return this.getOrInsertComputed(key, () => defaultValue);
  }

  getOrInsertComputed(key: K, defaultValueFn: (key: K) => V): V {
    const _key = this.keyToString(key);
    let value = this.map.get(_key);

    if (!value) {
      value = [key, defaultValueFn(key)];
      this.map.set(_key, value);
    }

    return value[1];
  }

  [Symbol.toStringTag]: string = 'MultiKeyMap';
}
